/**
 * **Add or edit a plan** — its name, description, order, fee and website
 * switch (C25, Wave 27D, T-1.31; ADR-0054 §4–§5). Until Wave 27 a plan could
 * only be named here and priced in a second dialog.
 *
 * The fee fields are the fee dialog's (Wave 15F), moved here so both dialogs
 * draw one set: `PriceFields`, `priceForm` and `pricingBody`. An empty fee is
 * **price on request** — a real answer, and where every plan an imported
 * template starts. *Show on our website* is the plan's own switch: a hidden
 * plan is not drawn by the website's pricing section, sets no *from* price and
 * is not offered by a booking flow that offers its service's plans without
 * naming them; a flow that names it, and a signed-in client in the portal,
 * still see it.
 */

import { Button, Dialog, Input, Select, Switch, Textarea } from '@fiveox/ui'
import { useEffect, useState } from 'react'

import { DialogActions, FORM_GRID, FormRow } from '../../components/page'
import { ApiError } from '../../lib/api'
import { type Plan, type Vocabulary, planShown, services } from '../../lib/api/services'
import { orderValue } from './CategoryDialog'

export interface PriceForm {
  amount: string
  gst_treatment: 'taxable' | 'exempt'
  gst_rate: string
  billing_cadence: string
}

export function priceForm(plan: Plan | null): PriceForm {
  return {
    amount: plan?.pricing?.amount ?? '',
    gst_treatment: plan?.pricing?.gst_treatment ?? 'taxable',
    gst_rate: plan?.pricing?.gst_rate ?? '18',
    billing_cadence: plan?.pricing?.billing_cadence ?? '',
  }
}

/** What the service is sent for a fee. A string amount, both ways: a rupee
 *  amount that has been through a JavaScript `number` may have been rounded. */
export function pricingBody(form: PriceForm) {
  return {
    amount: form.amount || '0',
    gst_treatment: form.gst_treatment,
    gst_rate: form.gst_treatment === 'taxable' ? form.gst_rate : null,
    billing_cadence: form.billing_cadence || null,
  }
}

/** The fee's four fields. The cadence only for a service that repeats. */
export function PriceFields({
  form,
  onChange,
  recurring,
  vocabulary,
  idBase,
  feeHint,
}: {
  form: PriceForm
  onChange: (form: PriceForm) => void
  recurring: boolean
  vocabulary: Vocabulary | null
  idBase: string
  feeHint?: string
}) {
  return (
    <>
      <FormRow label="Fee (₹)" htmlFor={`${idBase}-amount`}>
        <Input
          id={`${idBase}-amount`}
          value={form.amount}
          inputMode="decimal"
          hint={feeHint}
          onChange={(e) => onChange({ ...form, amount: e.target.value })}
        />
      </FormRow>
      <FormRow label="GST" htmlFor={`${idBase}-gst`}>
        <Select
          id={`${idBase}-gst`}
          value={form.gst_treatment}
          onChange={(e) => onChange({ ...form, gst_treatment: e.target.value as 'taxable' | 'exempt' })}
        >
          {(vocabulary?.gst_treatments ?? ['taxable', 'exempt']).map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </Select>
      </FormRow>
      {form.gst_treatment === 'taxable' && (
        <FormRow label="Rate (%)" htmlFor={`${idBase}-rate`}>
          <Input
            id={`${idBase}-rate`}
            value={form.gst_rate}
            inputMode="decimal"
            onChange={(e) => onChange({ ...form, gst_rate: e.target.value })}
          />
        </FormRow>
      )}
      {recurring && (
        <FormRow label="Billed" htmlFor={`${idBase}-cadence`} optional>
          <Select
            id={`${idBase}-cadence`}
            value={form.billing_cadence}
            onChange={(e) => onChange({ ...form, billing_cadence: e.target.value })}
          >
            <option value="">not set</option>
            {(vocabulary?.billing_cadences ?? []).map((c) => (
              <option key={c} value={c}>
                {c.replace(/_/g, ' ')}
              </option>
            ))}
          </Select>
        </FormRow>
      )}
    </>
  )
}

interface Form {
  name: string
  description: string
  order: string
  price: PriceForm
  site_visible: boolean
}

function formFor(plan: Plan | null): Form {
  return {
    name: plan?.name ?? '',
    description: plan?.description ?? '',
    order: plan ? String(plan.display_order) : '',
    price: priceForm(plan),
    site_visible: plan ? planShown(plan) : true,
  }
}

export function PlanDialog({
  open,
  serviceId,
  plan,
  recurring,
  vocabulary,
  onClose,
  onSaved,
}: {
  open: boolean
  serviceId: string
  /** `null` adds one. */
  plan: Plan | null
  recurring: boolean
  vocabulary: Vocabulary | null
  onClose: () => void
  onSaved: (sentence: string) => Promise<void> | void
}) {
  const [form, setForm] = useState<Form>(formFor(null))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setError(null)
    setForm(formFor(plan))
  }, [open, plan])

  const orderOk = form.order.trim() === '' || orderValue(form.order) !== undefined
  const ready = form.name.trim() !== '' && orderOk
  const priced = form.price.amount.trim() !== ''

  const save = async () => {
    setBusy(true)
    setError(null)
    const order = orderValue(form.order)
    const name = form.name.trim()
    try {
      if (plan) {
        await services.updatePlan(plan.id, {
          name,
          description: form.description.trim() || null,
          ...(order !== undefined ? { display_order: order } : {}),
          site_visible: form.site_visible,
          // An emptied fee is *price on request* — said with its own flag,
          // because a missing `pricing` on a PATCH means "not touched".
          ...(priced ? { pricing: pricingBody(form.price) } : plan.priced ? { clear_pricing: true } : {}),
        })
        await onSaved(`“${name}” is saved.`)
      } else {
        await services.createPlan(serviceId, {
          name,
          ...(form.description.trim() ? { description: form.description.trim() } : {}),
          ...(order !== undefined ? { display_order: order } : {}),
          ...(priced ? { pricing: pricingBody(form.price) } : {}),
          site_visible: form.site_visible,
        })
        await onSaved(`“${name}” is added.`)
      }
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That plan could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} title={plan ? `Edit ${plan.name}` : 'Add a plan'} error={error} width="lg">
      <div className={FORM_GRID}>
        <FormRow label="Name" htmlFor="plan-name">
          <Input
            id="plan-name"
            value={form.name}
            maxLength={120}
            placeholder="Standard"
            hint={plan ? undefined : 'A variant of this service — Basic, Standard, Premium.'}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </FormRow>
        <FormRow label="Description" htmlFor="plan-description" optional>
          <Textarea
            id="plan-description"
            rows={2}
            maxLength={1000}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </FormRow>
        <FormRow label="Order" htmlFor="plan-order" optional>
          <Input
            id="plan-order"
            inputMode="numeric"
            value={form.order}
            onChange={(e) => setForm({ ...form, order: e.target.value })}
            error={orderOk ? null : 'A whole number — lower comes first.'}
            hint={orderOk ? 'Lower comes first among this service’s plans.' : undefined}
          />
        </FormRow>
        <PriceFields
          form={form.price}
          onChange={(price) => setForm({ ...form, price })}
          recurring={recurring}
          vocabulary={vocabulary}
          idBase="plan"
          feeHint={priced ? undefined : 'Empty is price on request.'}
        />
        <FormRow label="Website">
          <Switch
            checked={form.site_visible}
            label="Show on our website"
            onChange={(e) => setForm({ ...form, site_visible: e.target.checked })}
          />
        </FormRow>
      </div>
      <DialogActions>
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button onClick={() => void save()} disabled={!ready} loading={busy}>
          {plan ? 'Save plan' : 'Add plan'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
